import { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/leadWebhook";

// Partner-program signup: captures referral partners (accountants, consultants,
// realtors, freelancers, industry connectors) via the single canonical webhook
// (source: 'partner_signup'). Same spam protection as FundingQualifierForm:
// hidden honeypot + render-to-submit time-trap.

type Status = "idle" | "sending" | "done";

const PARTNER_TYPES = [
  "Accountant / bookkeeper",
  "Business consultant / coach",
  "Realtor / real-estate network",
  "Marketing or web freelancer",
  "Automotive industry",
  "Other",
];

export function PartnerSignupForm() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company: "",
    email: "",
    phone: "",
    partner_type: "",
    network: "",
    in_mind: "",
  });
  const [consentResponse, setConsentResponse] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const mountedAt = useRef<number>(Date.now());

  useEffect(() => {
    mountedAt.current = Date.now();
  }, []);

  const update =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (honeypot.trim() !== "" || Date.now() - mountedAt.current < 2000) {
      setStatus("done"); // silent success for bots; nothing is written
      return;
    }

    if (!form.first_name || !form.email || !form.phone) {
      setError("Please add your name, email, and phone.");
      return;
    }
    if (!consentResponse) {
      setError("Please agree to let us reply to your application.");
      return;
    }

    setStatus("sending");
    try {
      const res = await submitLead("partner_signup", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.toLowerCase().trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        business_type: form.partner_type,
        interested_solution: "Partner Program",
        message: form.in_mind.trim(),
        details: [
          "Partner program application.",
          form.partner_type ? `Type: ${form.partner_type}.` : "",
          form.network.trim() ? `Network: ${form.network.trim()}` : "",
        ]
          .filter(Boolean)
          .join(" "),
        form_location: "partners_page",
        consent_response: consentResponse ? "yes" : "no",
        consent_marketing: consentMarketing ? "yes" : "no",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("done");
    } catch {
      setStatus("idle");
      setError("Something went wrong. Please call us at 807-700-0079 instead.");
    }
  }

  if (status === "done") {
    return (
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
        <h3 className="mt-4 text-xl font-bold">Application received.</h3>
        <p className="mx-auto mt-2 max-w-md text-muted-foreground">
          We'll reach out within one business day to walk through how the program
          works and get your partner agreement in place.
        </p>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20";

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-border bg-card p-6 text-foreground shadow-2xl shadow-black/30 ring-1 ring-black/5 sm:p-8"
    >
      <h3 className="text-xl font-bold sm:text-2xl">Become a partner</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        A few details and we'll set up a short call to get you started. No quota,
        no obligation. Refer when it genuinely fits.
      </p>

      {/* Honeypot: visually hidden, off the tab order, never autofilled by humans */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="partner_website">Company website (leave blank)</label>
        <input
          id="partner_website"
          name="partner_website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium">First name *</label>
          <input className={inputClass} value={form.first_name} onChange={update("first_name")} required />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Last name</label>
          <input className={inputClass} value={form.last_name} onChange={update("last_name")} />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium">Business / practice name</label>
          <input className={inputClass} value={form.company} onChange={update("company")} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Email *</label>
          <input className={inputClass} type="email" value={form.email} onChange={update("email")} required />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Phone *</label>
          <input className={inputClass} type="tel" value={form.phone} onChange={update("phone")} required />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium">What best describes you?</label>
          <select className={inputClass} value={form.partner_type} onChange={update("partner_type")}>
            <option value="">Select one…</option>
            {PARTNER_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium">Who do you typically work with?</label>
          <textarea
            className={inputClass}
            rows={2}
            placeholder="e.g. 40 small-business bookkeeping clients across Thunder Bay"
            value={form.network}
            onChange={update("network")}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium">Anyone in mind already? (optional)</label>
          <textarea
            className={inputClass}
            rows={2}
            placeholder="A business you'd refer first. No names required, a category is fine"
            value={form.in_mind}
            onChange={update("in_mind")}
          />
        </div>
      </div>

      <div className="mt-5 space-y-3 text-sm">
        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 flex-none"
            checked={consentResponse}
            onChange={(e) => setConsentResponse(e.target.checked)}
          />
          <span className="text-muted-foreground">
            I agree that Frayze can contact me by email and text about this
            application. Reply STOP to opt out at any time. *
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 flex-none"
            checked={consentMarketing}
            onChange={(e) => setConsentMarketing(e.target.checked)}
          />
          <span className="text-muted-foreground">
            Optional: send me occasional Frayze partner updates.
          </span>
        </label>
      </div>

      {error && <p className="mt-4 text-sm font-medium text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={status === "sending"}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white transition hover:bg-primary-hover disabled:opacity-60 sm:w-auto"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Sending...
          </>
        ) : (
          <>
            Apply to the partner program <ArrowRight className="h-5 w-5" />
          </>
        )}
      </button>
    </form>
  );
}
